import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Anchor, Search, ChevronDown, ChevronUp, AlertTriangle, Ship, ArrowUpRight, ArrowDownRight, Minus, MapPin, Filter, X, Gauge, Cloud, FileText, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NavBar } from '@/components/NavBar';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '@/lib/api';
import { useUserProfile } from '@/lib/useUserProfile';

const STATUS_CONFIG = {
  Normal: { color: '#2E7D32', bg: 'bg-[#2E7D32]/10', text: 'text-[#2E7D32]', border: 'border-[#2E7D32]/30' },
  Delays: { color: '#FF9500', bg: 'bg-[#FF9500]/10', text: 'text-[#FF9500]', border: 'border-[#FF9500]/30' },
  Congested: { color: '#FF6B00', bg: 'bg-[#FF6B00]/10', text: 'text-[#FF6B00]', border: 'border-[#FF6B00]/30' },
  Disrupted: { color: '#FF3B30', bg: 'bg-[#FF3B30]/10', text: 'text-[#FF3B30]', border: 'border-[#FF3B30]/30' },
  Closed: { color: '#8B0000', bg: 'bg-[#8B0000]/10', text: 'text-[#8B0000]', border: 'border-[#8B0000]/30' },
  Watch: { color: '#FF9500', bg: 'bg-[#FF9500]/10', text: 'text-[#FF9500]', border: 'border-[#FF9500]/30' },
  Elevated: { color: '#FF3B30', bg: 'bg-[#FF3B30]/10', text: 'text-[#FF3B30]', border: 'border-[#FF3B30]/30' },
};

const ROUTE_COLORS = { Normal: '#2E7D32', Delayed: '#FF9500', Disrupted: '#FF3B30' };

const PortsRoutesPage = () => {
  const navigate = useNavigate();
  const { profile, hasProfile, monitoredCountries, tradeFootprint, homeCountry } = useUserProfile();
  const [ports, setPorts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [chokepoints, setChokepoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [myPortsOnly, setMyPortsOnly] = useState(false);
  const [expandedPort, setExpandedPort] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [currencyMode, setCurrencyMode] = useState('usd'); // usd or aud
  const [weatherEvents, setWeatherEvents] = useState([]);
  const [showWeather, setShowWeather] = useState(true);
  const AUD_RATE = 1.55; // USD to AUD conversion

  const userPorts = useMemo(() => profile?.selected_ports || [], [profile]);
  const hasUserPorts = userPorts.length > 0;

  useEffect(() => {
    const load = async () => {
      try {
        const [portsRes, routesRes, chokepointsRes, weatherRes] = await Promise.all([
          api.getPortsRoutesPorts({ region: regionFilter !== 'all' ? regionFilter : undefined }),
          api.getPortsRoutesRoutes(homeCountry),
          api.getPortsRoutesChokepoints(),
          api.getWeatherEvents(),
        ]);
        setPorts(portsRes.data?.ports || []);
        setRoutes(routesRes.data?.routes || []);
        setChokepoints(chokepointsRes.data?.chokepoints || []);
        setWeatherEvents(weatherRes.data?.events || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter, homeCountry]);

  // My ports: user-selected or top ports from footprint countries
  const myPorts = useMemo(() => {
    if (hasUserPorts) return ports.filter(p => userPorts.includes(p.port_id));
    const fpCodes = Object.keys(tradeFootprint || {});
    if (fpCodes.length > 0) {
      return ports.filter(p => fpCodes.includes(p.country_code) || p.country_code === homeCountry).slice(0, 12);
    }
    return ports.filter(p => p.type !== 'destination').slice(0, 10);
  }, [ports, hasUserPorts, userPorts, tradeFootprint, homeCountry]);

  const filteredPorts = useMemo(() => {
    let result = myPortsOnly ? myPorts : ports;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(p => p.port_name.toLowerCase().includes(s) || p.country_name.toLowerCase().includes(s));
    }
    return result;
  }, [ports, myPorts, myPortsOnly, search]);

  const alertPorts = useMemo(() => myPorts.filter(p => ['Congested', 'Disrupted', 'Closed'].includes(p.status)), [myPorts]);

  const Trend = ({ val }) => {
    if (val > 0.3) return <ArrowUpRight className="h-3.5 w-3.5 text-[#FF3B30]" />;
    if (val < -0.3) return <ArrowDownRight className="h-3.5 w-3.5 text-[#2E7D32]" />;
    return <Minus className="h-3.5 w-3.5 text-[#4E6974]" />;
  };

  const UtilGauge = ({ pct }) => {
    const color = pct > 85 ? '#FF3B30' : pct > 70 ? '#FF9500' : '#2E7D32';
    const angle = (pct / 100) * 180;
    return (
      <div className="relative w-10 h-5 overflow-hidden">
        <svg viewBox="0 0 40 20" className="w-full h-full">
          <path d="M4 18 A 16 16 0 0 1 36 18" fill="none" stroke="#E0E0E0" strokeWidth="3" strokeLinecap="round" />
          <path d="M4 18 A 16 16 0 0 1 36 18" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${angle * 0.28} 100`} />
        </svg>
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px] font-bold" style={{ color }}>{pct}%</span>
      </div>
    );
  };

  // Route line points for map
  const routeLines = useMemo(() => {
    return routes.map(r => {
      const origin = ports.find(p => p.port_id === r.origin_port_id);
      const dest = ports.find(p => p.port_id === r.dest_port_id);
      if (!origin || !dest) return null;
      // Create curved arc via midpoint
      const midLat = (origin.lat + dest.lat) / 2 + (Math.abs(origin.lon - dest.lon) > 50 ? 8 : 3);
      const midLon = (origin.lon + dest.lon) / 2;
      return {
        ...r,
        positions: [[origin.lat, origin.lon], [midLat, midLon], [dest.lat, dest.lon]],
        color: ROUTE_COLORS[r.status] || ROUTE_COLORS.Normal,
      };
    }).filter(Boolean);
  }, [routes, ports]);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="ports-routes-title">Ports & routes</h1>
                <p className="text-[#4E6974] mt-1">{ports.length} ports monitored · {routes.length} active trade lanes · {chokepoints.length} chokepoints tracked</p>
              </div>
            </div>
          </motion.div>

          {/* Filter bar */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4E6974]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ports by name or country..." className="pl-9" data-testid="ports-search" />
                </div>
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="ports-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All regions</SelectItem>
                    <SelectItem value="apac">Asia-Pacific</SelectItem>
                    <SelectItem value="americas">Americas</SelectItem>
                    <SelectItem value="europe">Europe</SelectItem>
                    <SelectItem value="mea">Middle East & Africa</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => setMyPortsOnly(!myPortsOnly)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${myPortsOnly ? 'bg-[#00707C] text-white' : 'bg-[#F5F5F5] text-[#4E6974] hover:text-[#0D3B4F]'}`}
                  data-testid="my-ports-toggle">
                  <MapPin className="h-4 w-4" /> {myPortsOnly ? 'My ports' : 'All ports'}
                </button>
                <button onClick={() => setCurrencyMode(currencyMode === 'usd' ? 'aud' : 'usd')}
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-[#F5F5F5] text-[#4E6974] hover:text-[#0D3B4F] transition-all"
                  data-testid="currency-toggle">
                  {currencyMode === 'usd' ? 'USD' : 'AUD'}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Alert banner for disrupted ports */}
          {alertPorts.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6" data-testid="port-alert-banner">
              {alertPorts.map(p => (
                <div key={p.port_id} className="flex items-center gap-3 p-3 mb-2 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20">
                  <AlertTriangle className="h-5 w-5 text-[#FF3B30] flex-shrink-0" />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#0D3B4F]">{p.flag_emoji} {p.port_name} is experiencing {p.status.toLowerCase()}.</span>
                    <span className="text-sm text-[#4E6974] ml-1">{p.status_reason}</span>
                  </div>
                  <button onClick={() => setExpandedPort(expandedPort === p.port_id ? null : p.port_id)}
                    className="text-[#0097A7] text-xs font-medium hover:underline whitespace-nowrap">View alternatives</button>
                </div>
              ))}
            </motion.div>
          )}

          {/* Section 1: My ports at a glance */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#0D3B4F] mb-3" data-testid="my-ports-heading">
              {hasUserPorts ? 'My ports at a glance' : 'Key ports in your trade footprint'}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin" data-testid="port-status-cards">
              {myPorts.map((p, i) => {
                const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.Normal;
                return (
                  <motion.div key={p.port_id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                    className="flex-shrink-0 w-[220px]">
                    <Card className={`bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-none hover:shadow-md transition-all cursor-pointer ${expandedPort === p.port_id ? 'ring-2 ring-[#0097A7]' : ''}`}
                      onClick={() => setExpandedPort(expandedPort === p.port_id ? null : p.port_id)}
                      data-testid={`port-card-${p.port_id.toLowerCase()}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-base">{p.flag_emoji}</span>
                            <span className="text-sm font-bold text-[#0D3B4F] truncate max-w-[120px]">{p.port_name}</span>
                          </div>
                          <Badge className={`${sc.bg} ${sc.text} text-[9px] border ${sc.border}`}>{p.status}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-[#4E6974]">Wait</span>
                            <div className="flex items-center gap-1">
                              <span className="font-bold text-[#0D3B4F]">{p.wait_days}d</span>
                              <Trend val={p.wait_trend_vs_week} />
                            </div>
                          </div>
                          <div>
                            <span className="text-[#4E6974]">Dwell</span>
                            <div className="flex items-center gap-1">
                              <span className="font-bold text-[#0D3B4F]">{p.dwell_days}d</span>
                              <Trend val={p.dwell_trend_vs_week} />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <UtilGauge pct={p.utilisation_pct} />
                          <span className="text-[9px] text-[#4E6974]">Updated now</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Trade Route Map */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Live trade route map</CardTitle>
                  <div className="flex items-center gap-3 text-xs text-[#4E6974]">
                    <button onClick={() => setShowWeather(!showWeather)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all ${showWeather ? 'bg-[#0097A7]/10 text-[#0097A7]' : 'text-[#4E6974] hover:text-[#0D3B4F]'}`}
                      data-testid="weather-toggle">
                      <Cloud className="h-3 w-3" /> Weather
                    </button>
                    <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#2E7D32] rounded" /> Normal</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#FF9500] rounded" /> Delayed</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#FF3B30] rounded" /> Disrupted</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 h-[480px]" data-testid="trade-route-map">
                <MapContainer center={[10, 100]} zoom={3} scrollWheelZoom={true} style={{ height: '100%', width: '100%', borderRadius: '0 0 8px 8px' }}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; OpenStreetMap' />
                  {/* Route lines */}
                  {routeLines.map(r => (
                    <Polyline key={r.route_id} positions={r.positions} pathOptions={{ color: r.color, weight: 1.5, opacity: 0.6, dashArray: r.status === 'Delayed' ? '6 4' : undefined }}
                      eventHandlers={{ click: () => setSelectedRoute(r) }}>
                      <Popup>
                        <div className="p-1 text-xs">
                          <p className="font-bold">{r.origin_port_name} → {r.dest_port_name}</p>
                          <p>Transit: {r.transit_days} days · ${r.rate_teu_usd}/TEU</p>
                          <p>Status: {r.status} · Reliability: {r.reliability_pct}%</p>
                          <p className="text-[#0097A7] mt-1 cursor-pointer">Click for full route intelligence</p>
                        </div>
                      </Popup>
                    </Polyline>
                  ))}
                  {/* Port markers */}
                  {filteredPorts.map(p => {
                    const isMyPort = myPorts.some(mp => mp.port_id === p.port_id);
                    const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.Normal;
                    return (
                      <CircleMarker key={p.port_id} center={[p.lat, p.lon]}
                        radius={isMyPort ? 8 : 5}
                        fillColor={sc.color} fillOpacity={isMyPort ? 0.9 : 0.6}
                        stroke={true} weight={isMyPort ? 2 : 1} color={isMyPort ? '#4DD0E1' : '#fff'}
                        eventHandlers={{ click: () => setExpandedPort(expandedPort === p.port_id ? null : p.port_id) }}>
                        <Popup>
                          <div className="p-1 text-xs">
                            <p className="font-bold">{p.flag_emoji} {p.port_name}</p>
                            <p>Status: <span style={{ color: sc.color }}>{p.status}</span></p>
                            <p>Wait: {p.wait_days}d · Dwell: {p.dwell_days}d</p>
                            {isMyPort && <Badge className="bg-[#00707C] text-white text-[8px] mt-1">My port</Badge>}
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                  {/* Chokepoint markers */}
                  {chokepoints.map(c => {
                    const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.Normal;
                    return (
                      <CircleMarker key={c.id} center={[c.lat, c.lon]} radius={6}
                        fillColor={c.risk_level === 'high' ? '#FF3B30' : c.risk_level === 'elevated' ? '#FF9500' : '#4E6974'}
                        fillOpacity={0.8} stroke={true} weight={2} color="#fff">
                        <Popup>
                          <div className="p-1 text-xs">
                            <p className="font-bold">{c.name}</p>
                            <p>Status: <span style={{ color: sc.color }}>{c.status}</span></p>
                            <p className="max-w-[200px]">{c.description}</p>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                  {/* Weather event overlays */}
                  {showWeather && weatherEvents.map(w => (
                    <CircleMarker key={w.id} center={[w.lat, w.lon]}
                      radius={w.severity === 'High' ? 20 : w.severity === 'Moderate' ? 15 : 10}
                      fillColor={w.severity === 'High' ? '#FF3B30' : w.severity === 'Moderate' ? '#FF9500' : '#4E6974'}
                      fillOpacity={0.15} stroke={true} weight={1.5}
                      color={w.severity === 'High' ? '#FF3B30' : w.severity === 'Moderate' ? '#FF9500' : '#4E6974'}
                      dashArray="4 3">
                      <Popup>
                        <div className="p-1 text-xs max-w-[220px]">
                          <p className="font-bold flex items-center gap-1"><Cloud className="h-3 w-3" /> {w.name}</p>
                          <p className="text-[#4E6974]">{w.type} · {w.severity} severity · {w.duration}</p>
                          <p className="mt-1 text-[#4E6974]">{w.description}</p>
                          {w.affected_ports?.length > 0 && <p className="mt-1 text-[#FF9500]">Affects: {w.affected_ports.join(', ')}</p>}
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Section 3: Port Detail Panels */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#0D3B4F] mb-3">Port intelligence</h2>
            <div className="space-y-3" data-testid="port-detail-panels">
              {filteredPorts.map((p, i) => (
                <PortPanel key={p.port_id} port={p} expanded={expandedPort === p.port_id}
                  onToggle={() => setExpandedPort(expandedPort === p.port_id ? null : p.port_id)}
                  isMyPort={myPorts.some(mp => mp.port_id === p.port_id)} />
              ))}
              {filteredPorts.length === 0 && !loading && (
                <div className="text-center py-12 text-[#4E6974]">No ports match your filters</div>
              )}
            </div>
          </div>

          {/* Section 4: Chokepoints Summary */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Maritime chokepoints</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="chokepoints-grid">
                  {chokepoints.map(c => {
                    const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.Normal;
                    return (
                      <div key={c.id} className={`p-3 rounded-lg border ${sc.border} ${sc.bg}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-[#0D3B4F]">{c.name}</span>
                          <Badge className={`${sc.bg} ${sc.text} text-[9px]`}>{c.status}</Badge>
                        </div>
                        <p className="text-[10px] text-[#4E6974] line-clamp-2">{c.description}</p>
                        <p className="text-[10px] text-[#4E6974] mt-1">{c.daily_vessels} vessels/day</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Section 5: Freight Rate Intelligence */}
          {routes.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-6">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F]">Freight rate intelligence</CardTitle>
                    <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">{currencyMode.toUpperCase()}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto" data-testid="freight-rate-table">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[#4E6974] border-b border-[#E0E0E0]">
                          <th className="text-left py-2 font-medium">Route</th>
                          <th className="text-right py-2 font-medium">Rate/TEU</th>
                          <th className="text-right py-2 font-medium">Rate/FEU</th>
                          <th className="text-right py-2 font-medium">30d</th>
                          <th className="text-right py-2 font-medium">90d</th>
                          <th className="text-right py-2 font-medium">YoY</th>
                          <th className="text-right py-2 font-medium">Transit</th>
                          <th className="text-right py-2 font-medium">Reliability</th>
                          <th className="text-right py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routes.slice(0, 20).map(r => {
                          const fx = currencyMode === 'aud' ? AUD_RATE : 1;
                          const sym = currencyMode === 'aud' ? 'A$' : '$';
                          return (
                            <tr key={r.route_id} className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5] cursor-pointer transition-colors"
                              onClick={() => setSelectedRoute(r)} data-testid={`freight-row-${r.route_id}`}>
                              <td className="py-2 font-medium text-[#0D3B4F]">{r.origin_port_name} → {r.dest_port_name}</td>
                              <td className="py-2 text-right tabular-nums text-[#0D3B4F]">{sym}{Math.round(r.rate_teu_usd * fx).toLocaleString()}</td>
                              <td className="py-2 text-right tabular-nums text-[#4E6974]">{sym}{Math.round(r.rate_feu_usd * fx).toLocaleString()}</td>
                              <td className={`py-2 text-right tabular-nums font-medium ${r.change_30d_pct > 5 ? 'text-[#FF3B30]' : r.change_30d_pct < -5 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                                {r.change_30d_pct > 0 ? '+' : ''}{r.change_30d_pct}%
                              </td>
                              <td className={`py-2 text-right tabular-nums ${r.change_90d_pct > 5 ? 'text-[#FF3B30]' : r.change_90d_pct < -5 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                                {r.change_90d_pct > 0 ? '+' : ''}{r.change_90d_pct}%
                              </td>
                              <td className={`py-2 text-right tabular-nums ${r.change_yoy_pct > 10 ? 'text-[#FF3B30]' : r.change_yoy_pct < -10 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                                {r.change_yoy_pct > 0 ? '+' : ''}{r.change_yoy_pct}%
                              </td>
                              <td className="py-2 text-right text-[#0D3B4F]">{r.transit_days}d</td>
                              <td className="py-2 text-right text-[#0D3B4F]">{r.reliability_pct}%</td>
                              <td className="py-2 text-right">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${r.status === 'Normal' ? 'bg-[#2E7D32]/10 text-[#2E7D32]' : 'bg-[#FF9500]/10 text-[#FF9500]'}`}>
                                  {r.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </main>

      {/* Route Detail Drawer */}
      <AnimatePresence>
        {selectedRoute && (
          <RouteDrawer route={selectedRoute} onClose={() => setSelectedRoute(null)} currencyMode={currencyMode} audRate={AUD_RATE} homeCountry={homeCountry} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Port Panel Component ───
const PortPanel = ({ port, expanded, onToggle, isMyPort, homeCountry = 'AU' }) => {
  const [detail, setDetail] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [customs, setCustoms] = useState(null);
  const sc = STATUS_CONFIG[port.status] || STATUS_CONFIG.Normal;

  useEffect(() => {
    if (expanded && !detail) {
      api.getPortsRoutesPortDetail(port.port_id, homeCountry).then(res => setDetail(res.data)).catch(console.error);
      api.getPortNarrative(port.port_id).then(res => setNarrative(res.data)).catch(console.error);
      api.getPortCustoms(port.port_id).then(res => setCustoms(res.data)).catch(console.error);
    }
  }, [expanded, port.port_id, detail, homeCountry]);

  return (
    <motion.div layout>
      <Card className={`bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-none transition-all ${expanded ? 'shadow-[0_4px_16px_rgba(0,0,0,0.08)]' : ''}`}>
        {/* Collapsed view */}
        <CardContent className="p-4 cursor-pointer" onClick={onToggle} data-testid={`port-panel-${port.port_id.toLowerCase()}`}>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-lg">{port.flag_emoji}</span>
              <span className="text-sm font-bold text-[#0D3B4F] truncate">{port.port_name}</span>
              {isMyPort && <Badge className="bg-[#0097A7]/10 text-[#0097A7] text-[9px] border border-[#0097A7]/20">My port</Badge>}
              <Badge className={`${sc.bg} ${sc.text} text-[9px] border ${sc.border}`}>{port.status}</Badge>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <div className="text-center">
                <span className="text-[#4E6974]">Wait</span>
                <div className="font-bold text-[#0D3B4F]">{port.wait_days}d</div>
              </div>
              <div className="text-center">
                <span className="text-[#4E6974]">Dwell</span>
                <div className="font-bold text-[#0D3B4F]">{port.dwell_days}d</div>
              </div>
              <div className="text-center">
                <span className="text-[#4E6974]">Util</span>
                <div className="font-bold text-[#0D3B4F]">{port.utilisation_pct}%</div>
              </div>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-[#4E6974]" /> : <ChevronDown className="h-4 w-4 text-[#4E6974]" />}
          </div>
        </CardContent>

        {/* Expanded detail */}
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="px-4 pb-4 border-t border-[#E0E0E0] pt-4">
                {!detail ? (
                  <div className="text-center py-8 text-[#4E6974] text-sm">Loading port intelligence...</div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Left: Metrics */}
                    <div className="lg:col-span-3 space-y-4">
                      <div>
                        <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Performance metrics</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: 'Vessel wait', value: `${port.wait_days}d`, sub: `90d avg: ${(port.wait_days - 0.3).toFixed(1)}d` },
                            { label: 'Container dwell', value: `${port.dwell_days}d`, sub: `90d avg: ${(port.dwell_days - 0.2).toFixed(1)}d` },
                            { label: 'Berth occupancy', value: `${detail.berth_occupancy_pct}%`, sub: detail.berth_occupancy_pct > 85 ? 'High pressure' : 'Normal' },
                            { label: 'Crane moves/hr', value: detail.crane_moves_hr, sub: `Capacity: ${detail.teu_capacity_m}M TEU` },
                          ].map(m => (
                            <div key={m.label} className="p-2.5 rounded-lg bg-[#F5F5F5]">
                              <span className="text-[10px] text-[#4E6974]">{m.label}</span>
                              <div className="text-lg font-bold text-[#0D3B4F]">{m.value}</div>
                              <span className="text-[10px] text-[#4E6974]">{m.sub}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Infrastructure</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          {[
                            { label: 'Max vessel', value: `${(detail.max_vessel_teu / 1000).toFixed(0)}K TEU` },
                            { label: 'Terminals', value: detail.terminals },
                            { label: 'Reefer plugs', value: detail.reefer },
                            { label: 'DG handling', value: detail.dg_handling ? 'Yes' : 'No' },
                            { label: 'Free trade zone', value: detail.ftz ? 'Yes' : 'No' },
                            { label: 'Rail', value: detail.rail },
                            { label: 'Hours', value: detail.hours },
                            { label: 'Emissions', value: detail.emissions_rating, color: detail.emissions_rating === 'A' ? '#2E7D32' : detail.emissions_rating === 'B' ? '#2E7D32' : detail.emissions_rating === 'C' ? '#FF9500' : '#FF3B30' },
                          ].map(item => (
                            <div key={item.label} className="flex justify-between p-1.5 rounded bg-[#F5F5F5]">
                              <span className="text-[#4E6974]">{item.label}</span>
                              <span className="font-medium text-[#0D3B4F]" style={item.color ? { color: item.color } : {}}>{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Routes from this port */}
                      {detail.routes?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Trade lanes</h4>
                          <div className="space-y-1">
                            {detail.routes.slice(0, 5).map(r => (
                              <div key={r.route_id} className="flex items-center justify-between p-2 rounded bg-[#F5F5F5] text-xs">
                                <span className="text-[#0D3B4F] font-medium">{r.origin_port_name} → {r.dest_port_name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-[#4E6974]">{r.transit_days}d</span>
                                  <span className="font-medium text-[#0D3B4F]">${r.rate_teu_usd}/TEU</span>
                                  <span className={r.change_30d_pct > 0 ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}>
                                    {r.change_30d_pct > 0 ? '+' : ''}{r.change_30d_pct}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Alerts + Alternatives */}
                    <div className="lg:col-span-2 space-y-4">
                      {port.status !== 'Normal' && (
                        <div className={`p-3 rounded-lg border ${sc.border} ${sc.bg}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="h-4 w-4" style={{ color: sc.color }} />
                            <span className="text-xs font-bold text-[#0D3B4F]">Active disruption</span>
                          </div>
                          <p className="text-xs text-[#4E6974]">{port.status_reason || `${port.port_name} is currently experiencing ${port.status.toLowerCase()} conditions.`}</p>
                        </div>
                      )}

                      <div>
                        <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Seasonal insights</h4>
                        <p className="text-xs text-[#4E6974]">Peak congestion: {port.peak_months?.join(', ') || 'Oct-Dec'}</p>
                        <p className="text-xs text-[#4E6974]">Customs clearance: ~{port.customs_clearance_days}d avg</p>
                      </div>

                      {/* Customs Intelligence */}
                      {customs && (
                        <div data-testid={`customs-${port.port_id.toLowerCase()}`}>
                          <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Customs intelligence</h4>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between p-1.5 rounded bg-[#F5F5F5]">
                              <span className="text-[#4E6974]">General clearance</span>
                              <span className="font-medium text-[#0D3B4F]">{customs.avg_clearance_days}d</span>
                            </div>
                            <div className="flex justify-between p-1.5 rounded bg-[#F5F5F5]">
                              <span className="text-[#4E6974]">Electronics clearance</span>
                              <span className="font-medium text-[#0D3B4F]">{customs.electronics_clearance_days}d</span>
                            </div>
                          </div>
                          {customs.recent_changes?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-[10px] text-[#4E6974] font-medium">Recent policy changes:</span>
                              {customs.recent_changes.map((c) => (
                                <p key={c} className="text-[10px] text-[#FF9500] mt-0.5">- {c}</p>
                              ))}
                            </div>
                          )}
                          {customs.docs_required?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-[10px] text-[#4E6974] font-medium">Required documents:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {customs.docs_required.map((d) => (
                                  <span key={d} className="px-1.5 py-0.5 bg-[#F5F5F5] rounded text-[9px] text-[#4E6974]">{d}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {detail.alternatives?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Alternatives to {port.port_name}</h4>
                          <div className="space-y-1.5">
                            {detail.alternatives.slice(0, 4).map(a => {
                              const altSc = STATUS_CONFIG[a.status] || STATUS_CONFIG.Normal;
                              return (
                                <div key={a.port_id} className="p-2 rounded-lg bg-[#F5F5F5] text-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-[#0D3B4F]">{a.flag_emoji} {a.port_name}</span>
                                    <Badge className={`${altSc.bg} ${altSc.text} text-[8px] border ${altSc.border}`}>{a.status}</Badge>
                                  </div>
                                  <p className="text-[#4E6974] mt-0.5">Wait: {a.wait_days}d · {a.wait_comparison}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* AI Port Narrative */}
                      {narrative && (
                        <div>
                          <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Port briefing
                            {narrative.source === 'ai' && <Badge className="bg-[#4DD0E1]/15 text-[#4DD0E1] text-[8px] ml-2 border-none">AI</Badge>}
                          </h4>
                          <p className="text-xs text-[#4E6974] leading-relaxed whitespace-pre-line" data-testid={`port-narrative-${port.port_id.toLowerCase()}`}>{narrative.narrative}</p>
                        </div>
                      )}
                      {!narrative && expanded && (
                        <div className="flex items-center gap-2 text-xs text-[#4E6974]">
                          <div className="h-1.5 w-1.5 rounded-full bg-[#00707C] animate-pulse" />
                          Generating port briefing...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

// ─── Route Detail Drawer ───
const RouteDrawer = ({ route, onClose, currencyMode, audRate, homeCountry = 'AU' }) => {
  const [detail, setDetail] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [carriers, setCarriers] = useState(null);
  const fx = currencyMode === 'aud' ? audRate : 1;
  const sym = currencyMode === 'aud' ? 'A$' : '$';

  useEffect(() => {
    api.getPortsRoutesRouteDetail(route.route_id, homeCountry).then(res => setDetail(res.data)).catch(console.error);
    api.getRouteNarrative(route.route_id, homeCountry).then(res => setNarrative(res.data)).catch(console.error);
    api.getRouteCarriers(route.route_id).then(res => setCarriers(res.data?.carriers)).catch(console.error);
  }, [route.route_id, homeCountry]);

  const sc = STATUS_CONFIG[route.status === 'Delayed' ? 'Delays' : route.status] || STATUS_CONFIG.Normal;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100]" data-testid="route-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 250 }}
        className="absolute right-0 top-0 h-full w-full max-w-[600px] bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-[#0D3B4F] text-white px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold">{route.origin_port_name} → {route.dest_port_name}</h3>
              <p className="text-xs text-white/60 mt-0.5">Route intelligence</p>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-[#F5F5F5] text-center">
              <span className="text-[10px] text-[#4E6974] uppercase">Transit</span>
              <div className="text-xl font-bold text-[#0D3B4F]">{route.transit_days}d</div>
              <span className="text-[10px] text-[#4E6974]">90d avg: {route.transit_days_avg_90d}d</span>
            </div>
            <div className="p-3 rounded-lg bg-[#F5F5F5] text-center">
              <span className="text-[10px] text-[#4E6974] uppercase">Rate/TEU</span>
              <div className="text-xl font-bold text-[#0D3B4F]">{sym}{Math.round(route.rate_teu_usd * fx).toLocaleString()}</div>
              <span className={`text-[10px] font-medium ${route.change_30d_pct > 0 ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}`}>
                {route.change_30d_pct > 0 ? '+' : ''}{route.change_30d_pct}% 30d
              </span>
            </div>
            <div className="p-3 rounded-lg bg-[#F5F5F5] text-center">
              <span className="text-[10px] text-[#4E6974] uppercase">Reliability</span>
              <div className="text-xl font-bold text-[#0D3B4F]">{route.reliability_pct}%</div>
              <span className="text-[10px] text-[#4E6974]">On-time arrival</span>
            </div>
          </div>

          {/* Status */}
          <div className={`p-3 rounded-lg border ${sc.border} ${sc.bg}`}>
            <div className="flex items-center gap-2">
              <Badge className={`${sc.bg} ${sc.text} text-[9px]`}>{route.status}</Badge>
              <span className="text-xs text-[#0D3B4F]">{route.status_reason || 'Route operating normally'}</span>
            </div>
          </div>

          {/* Schedule reliability detail */}
          <div>
            <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Schedule reliability</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded bg-[#F5F5F5] flex justify-between"><span className="text-[#4E6974]">Avg delay when late</span><span className="font-medium text-[#0D3B4F]">{route.avg_delay_days}d</span></div>
              <div className="p-2 rounded bg-[#F5F5F5] flex justify-between"><span className="text-[#4E6974]">Blank sailings (90d)</span><span className="font-medium text-[#0D3B4F]">{route.blank_sailings_90d}</span></div>
              <div className="p-2 rounded bg-[#F5F5F5] flex justify-between"><span className="text-[#4E6974]">CO2/TEU-km</span><span className="font-medium text-[#0D3B4F]">{route.co2_per_teu_km} kg</span></div>
              <div className="p-2 rounded bg-[#F5F5F5] flex justify-between"><span className="text-[#4E6974]">Rate/FEU</span><span className="font-medium text-[#0D3B4F]">{sym}{Math.round(route.rate_feu_usd * fx).toLocaleString()}</span></div>
            </div>
          </div>

          {/* Chokepoints on this route */}
          {detail?.chokepoint_details?.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Chokepoints on this route</h4>
              <div className="space-y-1.5">
                {detail.chokepoint_details.map(c => {
                  const csc = STATUS_CONFIG[c.status] || STATUS_CONFIG.Normal;
                  return (
                    <div key={c.id} className={`p-2.5 rounded-lg border ${csc.border} ${csc.bg}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-[#0D3B4F]">{c.name}</span>
                        <Badge className={`${csc.bg} ${csc.text} text-[8px]`}>{c.status}</Badge>
                      </div>
                      <p className="text-[10px] text-[#4E6974] mt-0.5">{c.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Freight history */}
          {detail?.freight_history && (
            <div>
              <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Freight rate history (12 months + forecast)</h4>
              <div className="space-y-1">
                {detail.freight_history.map((h, i) => (
                  <div key={h.month} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${h.is_forecast ? 'bg-[#0097A7]/5 border border-dashed border-[#0097A7]/20' : i % 2 === 0 ? 'bg-[#F5F5F5]' : ''}`}>
                    <span className="text-[#4E6974]">{h.month} {h.is_forecast ? '(forecast)' : ''}</span>
                    <span className="font-medium text-[#0D3B4F]">{sym}{Math.round(h.rate_teu_usd * fx).toLocaleString()}/TEU</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Carrier reliability */}
          {carriers && carriers.length > 0 && (
            <div data-testid="route-carriers">
              <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Carrier reliability on this route</h4>
              <div className="space-y-1.5">
                {carriers.map((c, i) => (
                  <div key={c.carrier_name} className="flex items-center justify-between p-2 rounded bg-[#F5F5F5] text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#4E6974] w-4">{i + 1}.</span>
                      <span className="font-medium text-[#0D3B4F]">{c.carrier_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-medium ${c.on_time_pct >= 85 ? 'text-[#2E7D32]' : c.on_time_pct >= 80 ? 'text-[#FF9500]' : 'text-[#FF3B30]'}`}>{c.on_time_pct}% on-time</span>
                      <span className="text-[#4E6974]">{c.avg_delay_days}d avg delay</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Narrative */}
          <div>
            <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Route intelligence briefing
              {narrative?.source === 'ai' && <Badge className="bg-[#4DD0E1]/15 text-[#4DD0E1] text-[8px] ml-2 border-none">AI</Badge>}
            </h4>
            {narrative ? (
              <p className="text-xs text-[#4E6974] leading-relaxed" data-testid="route-narrative">{narrative.narrative}</p>
            ) : (
              <div className="flex items-center gap-2 text-xs text-[#4E6974]">
                <div className="h-1.5 w-1.5 rounded-full bg-[#00707C] animate-pulse" />
                Generating route briefing...
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PortsRoutesPage;
